import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../context/AuthContext";

/**
 * Compact monthly calendar for the business owner.
 * - Shows every confirmed/completed booking on its day with a colored dot per status
 * - Highlights today
 * - Clicking a day opens an inline list of that day's bookings, with the service title,
 *   customer name, and time range. Status chips: pending / confirmed / completed / cancelled.
 * - Double-booking is prevented at the booking-creation API level by /services/{id}/availability;
 *   this view is purely informational.
 */
const STATUS_COLOR = {
  pending_payment: "#d97706",
  confirmed:       "#16a34a",
  completed:       "var(--gf-accent)",
  declined:        "#dc2626",
  cancelled:       "var(--gf-text-muted)",
  refunded:        "var(--gf-text-muted)",
};

const STATUS_LABEL = {
  pending_payment: "Pending",
  confirmed:       "Confirmed",
  completed:       "Completed",
  declined:        "Declined",
  cancelled:       "Cancelled",
  refunded:        "Refunded",
};

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d)   { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function ymd(d)          { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; }

export default function BusinessCalendar() {
  const { authHeader } = useAuth();
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [bookings, setBookings] = useState([]);
  const [selected, setSelected] = useState(ymd(new Date()));

  useEffect(() => {
    axios.get(`${API}/bookings/business`, { headers: authHeader() })
      .then((r) => setBookings(r.data || []))
      .catch(() => setBookings([]));
    // eslint-disable-next-line
  }, []);

  // Group bookings by YYYY-MM-DD
  const byDay = useMemo(() => {
    const out = {};
    for (const b of bookings) {
      if (!b.scheduled_at) continue;
      const d = new Date(b.scheduled_at);
      if (isNaN(d.getTime())) continue;
      const k = ymd(d);
      (out[k] = out[k] || []).push(b);
    }
    // Sort each day chronologically
    Object.values(out).forEach((arr) => arr.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)));
    return out;
  }, [bookings]);

  // Build the visible grid: full weeks containing the month
  const gridDays = useMemo(() => {
    const first = startOfMonth(cursor);
    const last  = endOfMonth(cursor);
    const start = new Date(first);
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7)); // Monday-start
    const end = new Date(last);
    end.setDate(end.getDate() + (7 - ((end.getDay() + 6) % 7) - 1));
    const out = [];
    const cur = new Date(start);
    while (cur <= end) { out.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
    return out;
  }, [cursor]);

  const todayKey = ymd(new Date());
  const selectedDay = byDay[selected] || [];

  const prevMonth = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  const nextMonth = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));

  return (
    <div data-testid="business-calendar" className="mt-10 rounded-2xl border p-5"
         style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Calendar</div>
          <div className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }} data-testid="calendar-month-title">
            {cursor.toLocaleString(undefined, { month: "long", year: "numeric" })}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="calendar-prev-btn" onClick={prevMonth} className="rounded-full border w-8 h-8 inline-flex items-center justify-center"
                  style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>‹</button>
          <button data-testid="calendar-today-btn" onClick={() => { setCursor(startOfMonth(new Date())); setSelected(todayKey); }}
                  className="text-xs rounded-full border px-3 py-1"
                  style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>Today</button>
          <button data-testid="calendar-next-btn" onClick={nextMonth} className="rounded-full border w-8 h-8 inline-flex items-center justify-center"
                  style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>›</button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-7 gap-1 text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>
        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => (
          <div key={d} className="text-center pb-1">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {gridDays.map((d) => {
          const k = ymd(d);
          const inMonth = d.getMonth() === cursor.getMonth();
          const dayBookings = byDay[k] || [];
          const isToday = k === todayKey;
          const isSelected = k === selected;
          return (
            <button
              key={k}
              type="button"
              data-testid={`calendar-day-${k}`}
              onClick={() => setSelected(k)}
              className="aspect-square rounded-md flex flex-col items-center justify-start py-1.5 transition-colors text-left"
              style={{
                background: isSelected ? "var(--gf-accent)" : isToday ? "var(--gf-bg-soft)" : "transparent",
                color: isSelected ? "var(--gf-on-accent)" : inMonth ? "var(--gf-text)" : "var(--gf-text-muted)",
                border: `1px solid ${isSelected ? "var(--gf-accent)" : "var(--gf-border)"}`,
                opacity: inMonth ? 1 : 0.45,
              }}
            >
              <span className="text-xs font-medium leading-none">{d.getDate()}</span>
              <span className="mt-auto mb-0.5 flex items-center gap-0.5 flex-wrap justify-center max-w-full">
                {dayBookings.slice(0, 4).map((b) => (
                  <span key={b.id} className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_COLOR[b.status] || "var(--gf-text-muted)" }} />
                ))}
                {dayBookings.length > 4 && (
                  <span className="text-[9px] leading-none" style={{ color: isSelected ? "var(--gf-on-accent)" : "var(--gf-text-muted)" }}>+{dayBookings.length - 4}</span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-5">
        <div className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>
          {new Date(selected + "T00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
        </div>
        {selectedDay.length === 0 ? (
          <div data-testid="calendar-empty-day" className="mt-2 text-sm" style={{ color: "var(--gf-text-muted)" }}>No bookings on this day.</div>
        ) : (
          <ul className="mt-2 space-y-2" data-testid="calendar-day-list">
            {selectedDay.map((b) => {
              const t = new Date(b.scheduled_at);
              const end = new Date(t.getTime() + (Number(b.duration_minutes) || 60) * 60000);
              const time = `${t.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} – ${end.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
              return (
                <li key={b.id} data-testid={`calendar-booking-${b.id}`} className="rounded-md border p-3 flex items-center justify-between gap-3 flex-wrap"
                    style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg-soft)" }}>
                  <div className="min-w-[200px]">
                    <div className="text-sm font-medium" style={{ color: "var(--gf-text)" }}>{b.service_title}</div>
                    <div className="text-xs" style={{ color: "var(--gf-text-muted)" }}>{time} · {b.customer_name}</div>
                  </div>
                  <span className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.18em]"
                        style={{ background: STATUS_COLOR[b.status] || "var(--gf-text-muted)", color: "#fff" }}>
                    {STATUS_LABEL[b.status] || b.status}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
